import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import Button from './reusable/Button';

const selectOptions = [
	'Web Application',
	'Mobile Application',
	'Salesforce',
];

const HireMeModal = ({ onClose }) => {
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		subject: selectOptions[0],
		message: '',
	});
	const [status, setStatus] = useState({ type: null, message: '' });
	const [loading, setLoading] = useState(false);

	const handleChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setStatus({ type: null, message: '' });

		try {
			const res = await fetch('/api/send', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					...formData,
					subject: `Hire Me - ${formData.subject}`,
				}),
			});

			const text = await res.text();
			let data;
			try {
				data = JSON.parse(text);
			} catch {
				throw new Error('Server error. Please try again later.');
			}

			if (!res.ok) {
				throw new Error(data.error || 'Something went wrong.');
			}

			setStatus({ type: 'success', message: 'Request sent successfully!' });
			setFormData({ name: '', email: '', subject: selectOptions[0], message: '' });
		} catch (error) {
			setStatus({ type: 'error', message: error.message });
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			className="font-general-medium fixed inset-0 z-30 transition-all duration-500"
		>
			{/* Modal Backdrop */}
			<div className="bg-filter bg-black bg-opacity-50 fixed inset-0 w-full h-full z-20"></div>

			{/* Modal Content */}
			<main className="flex items-center justify-center h-full w-full px-4">
				<div className="modal-wrapper flex items-center justify-center z-30 w-full max-h-[90vh]">
					<div className="modal w-full max-w-md md:max-w-xl bg-secondary-light dark:bg-primary-dark shadow-lg rounded-lg relative flex flex-col max-h-[90vh]">
						<div className="modal-header flex justify-between items-center gap-4 p-4 sm:p-5 border-b border-ternary-light dark:border-ternary-dark shrink-0">
							<h5 className="text-primary-dark dark:text-primary-light text-lg sm:text-xl">
								What project are you looking for?
							</h5>
							<button
								onClick={onClose}
								className="p-2 font-bold text-primary-dark dark:text-primary-light"
							>
								<FiX className="text-2xl sm:text-3xl" />
							</button>
						</div>
						<div className="modal-body p-4 sm:p-5 overflow-y-auto">
							<AnimatePresence>
								{status.type && (
									<motion.div
										initial={{ opacity: 0, y: -10 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -10 }}
										transition={{ duration: 0.3 }}
										className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium border ${
											status.type === 'success'
												? 'border-green-500 bg-green-500/10 text-green-400'
												: 'border-red-500 bg-red-500/10 text-red-400'
										}`}
									>
										{status.type === 'success' ? '✓ ' : '✗ '}
										{status.message}
									</motion.div>
								)}
							</AnimatePresence>
							<form
								onSubmit={handleSubmit}
								className="text-left"
							>
								<div>
									<input
										className="w-full px-4 py-2 border dark:border-secondary-dark rounded-md text-md bg-secondary-light dark:bg-ternary-dark text-primary-dark dark:text-ternary-light"
										id="hire-name"
										name="name"
										type="text"
										required
										placeholder="Name"
										aria-label="Name"
										value={formData.name}
										onChange={handleChange}
									/>
								</div>
								<div className="mt-4 sm:mt-6">
									<input
										className="w-full px-4 py-2 border dark:border-secondary-dark rounded-md text-md bg-secondary-light dark:bg-ternary-dark text-primary-dark dark:text-ternary-light"
										id="hire-email"
										name="email"
										type="email"
										required
										placeholder="Email"
										aria-label="Email"
										value={formData.email}
										onChange={handleChange}
									/>
								</div>
								<div className="mt-4 sm:mt-6">
									<select
										className="w-full px-4 py-2 border dark:border-secondary-dark rounded-md text-md bg-secondary-light dark:bg-ternary-dark text-primary-dark dark:text-ternary-light"
										id="hire-subject"
										name="subject"
										required
										aria-label="Project Category"
										value={formData.subject}
										onChange={handleChange}
									>
										{selectOptions.map((option) => (
											<option
												className="text-normal sm:text-md"
												key={option}
											>
												{option}
											</option>
										))}
									</select>
								</div>

								<div className="mt-4 sm:mt-6">
									<textarea
										className="w-full px-4 py-2 border dark:border-secondary-dark rounded-md text-md bg-secondary-light dark:bg-ternary-dark text-primary-dark dark:text-ternary-light"
										id="hire-message"
										name="message"
										cols="14"
										rows="4"
										aria-label="Details"
										placeholder="Project description"
										required
										value={formData.message}
										onChange={handleChange}
									></textarea>
								</div>

								<div className="mt-4 sm:mt-6">
									<Button
										title={loading ? 'Sending...' : 'Send Request'}
										type="submit"
										disabled={loading}
										className="w-full sm:w-auto"
									/>
								</div>
							</form>
						</div>
						<div className="modal-footer py-4 px-4 sm:px-8 border-t border-ternary-light dark:border-ternary-dark text-right shrink-0">
							<button
								onClick={onClose}
								type="button"
								className="px-6 py-2 bg-gray-600 text-primary-light hover:bg-ternary-dark dark:bg-gray-200 dark:text-secondary-dark dark:hover:bg-primary-light rounded-md focus:ring-1 focus:ring-indigo-900 duration-500"
								aria-label="Close Modal"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			</main>
		</motion.div>
	);
};

export default HireMeModal;
